import UploadDocument from '@components/form/upload/uploadDocuments';
import { OETrans } from '@components/translation/OETrans';
import { translate } from '@components/translation/translation';
import { ProjectType } from '@lib/api_ts/projectApi';
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { Button, Modal, Tab, Tabs } from 'react-bootstrap';
import AdvancedSetupForm from './AdvancedSetupForm';
import RegistryForm, { RegistryFormHandle } from './RegistryForm';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { createProject } from '@lib/api_ts/projectsApi';
import { editProject } from '@lib/api_ts/projectApi';
import { RejectModal } from '@components/atomic/organisms';
import SuccessModal from '@components/atomic/organisms/SucessModal/SucessModal';
import { assignSamplingRuleToEntity, detachSamplingRuleToEntity } from '@lib/api';

interface EditModalProps {
    trigger: ReactElement<{
        onClick: () => void;
    }
    >;
    type: 'create' | 'edit';
    project?: ProjectType;
}

type OptionSamplingType = {
    value: number;
    label: string;
};

const EditModal: React.FC<EditModalProps> = ({ type, project, trigger }) => {
    const blankProject: Partial<ProjectType> = {
        cupType: 'C',
    }
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [activeKey, setActiveKey] = useState('registry');
    const [projectToSave, setProjectToSave] = useState<Partial<ProjectType> | undefined>(
        blankProject
    );
    const [selectedSamplingRule, setSelectedSamplingRule] = useState<OptionSamplingType[]>([]);
    const [invalidFields, setInvalidFields] = useState<string[]>([]);

    const formRef = useRef<RegistryFormHandle>(null);

    const queryClient = useQueryClient();

    useEffect(() => {
        if (!isModalOpen) return

        if (type === 'edit' && project) {
            const { createdAt, createdBy, updatedAt, updatedBy, ...projectToSubmit } = project;
            setProjectToSave({
                ...projectToSubmit,
                groupids: project.groups?.map(group => group.id),
            });
        }
    }, [project, type, isModalOpen]);

    const createProjectMutation = useMutation<Partial<ProjectType>, Error, Partial<ProjectType>>({
        mutationFn: (projectForMutation) => createProject(projectForMutation),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['projects'] });
        },
    });
    const editProjectMutation = useMutation<Partial<ProjectType>, Error, Partial<ProjectType>>({
        mutationFn: (projectForMutation) => editProject(projectToSave.id, projectForMutation),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['project', String(projectToSave.id)] });
        },
    });

    const saveSamplingRuleMutation = useMutation({
        mutationFn: async () => {
            const ruleId = selectedSamplingRule.length ? selectedSamplingRule[0].value : undefined;

            if (selectedSamplingRule.length && projectToSave.samplingRuleAssignment?.samplingRuleId !== ruleId) {
                await assignSamplingRuleToEntity(ruleId, 'projects', projectToSave.id);
            } else if (!selectedSamplingRule.length && projectToSave.samplingRuleAssignment?.samplingRuleId) {
                await detachSamplingRuleToEntity('projects', projectToSave.id);
            }
        },
        onError: (error) => {
            console.error('Error extending sampling:', error);
        },
    });


    const handleSave = async () => {
        const invalids = formRef.current?.validate();
        if (!invalids || invalids.length > 0) return;
        try {

            switch (type) {
                case 'create':
                    await createProjectMutation.mutateAsync(projectToSave);
                    setProjectToSave(
                        blankProject
                    );
                    break;
                case 'edit':
                    await saveSamplingRuleMutation.mutateAsync();
                    const result = await editProjectMutation.mutateAsync(projectToSave);
                    setProjectToSave(result);
                    break;
            }
            setIsModalOpen(false);
            setIsDirty(false);
        } catch (error) {
            // errore skippato, viene gestito da React Query
            console.error("Errore durante il salvataggio del progetto:", error);
            return;

        }
    };

    const triggerWithOnClick = React.isValidElement(trigger)
        ? React.cloneElement(trigger, {
            onClick: () => {
                setActiveKey('registry');
                setIsModalOpen(true)
            },
        })
        : trigger;

    return <>
        {triggerWithOnClick}
        <Modal
            show={isModalOpen}
            onHide={() => {
                setIsModalOpen(false)
                setProjectToSave(blankProject);
                setInvalidFields([]);
                setIsDirty(false);
            }}
            centered
            size='xl'
        >
            <Modal.Header closeButton>
                <Modal.Title className='h4'>{type === 'create' ? 'Nuovo progetto' : 'Modifica progetto'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {/* Form per la creazione/modifica del progetto */}
                <Tabs className='mb-4' variant='pills' activeKey={activeKey} onSelect={(key) => setActiveKey(key)} fill>
                    <Tab
                        eventKey="registry"
                        title={
                            <>
                                {translate('common.registry', 'Anagrafica')}

                                {invalidFields.length > 0 ?
                                    <span className='badge bg-danger ms-2 rounded-pill'>{invalidFields.length}</span> : null}
                            </>
                        }>
                        <RegistryForm
                            ref={formRef}
                            type={type}
                            projectToSave={projectToSave}
                            setProjectToSave={setProjectToSave}
                            // isSubmitting={isSubmitting}
                            invalidFields={invalidFields}
                            setInvalidFields={setInvalidFields}
                            isDirty={isDirty}
                            setIsDirty={setIsDirty}
                        /></Tab>

                    <Tab
                        eventKey="docs"
                        title={
                            <OETrans code="common.documents" fallback={'Documenti'} />
                        }
                        disabled={
                            type === 'create' || !project
                        }>
                        {!!project ? (
                            <UploadDocument entity="projects" entityId={project.id} noticeId={project.noticeId} />
                        ) : null}
                    </Tab>

                    <Tab
                        eventKey="advancedSettings"
                        title={translate('common.advancedSettings', 'Impostazioni avanzate')}
                        className="px-5"
                        disabled={!project || !project.id || !project?.advancedConfig || project?.advancedConfig?.length === 0}
                    >
                        {project?.advancedConfig && project?.advancedConfig.length > 0 ?
                            <AdvancedSetupForm 
                                activeKey={activeKey} 
                                projectToSave={projectToSave} 
                                setProjectToSave={setProjectToSave} 
                                isDirty={isDirty}
                                setIsDirty={setIsDirty} 
                                selectedSamplingRule={selectedSamplingRule} 
                                setSelectedSamplingRule={setSelectedSamplingRule}
                            />
                            : null}
                    </Tab>

                </Tabs>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    onClick={handleSave}
                    disabled={!isDirty || createProjectMutation.isPending || editProjectMutation.isPending || invalidFields.length > 0}
                    className='btn custom-primary-button'>

                    {type === 'create' ? 'Crea' : 'Modifica'}
                </Button>
            </Modal.Footer>
        </Modal>
        <SuccessModal
            message={
                type === 'create' ?
                    'Progetto creato con successo!' :
                    'Progetto modificato con successo!'
            }
            show={
                type === 'create' ?
                    createProjectMutation.isSuccess :
                    editProjectMutation.isSuccess
            }
            onHide={
                type === 'create' ?
                    createProjectMutation.reset :
                    editProjectMutation.reset
            }
        />
        <RejectModal
            message={
                type === 'create' ? createProjectMutation.error?.message ||
                    'Errore nella creazione del progetto!' :
                    editProjectMutation.error?.message ||
                    'Errore nella modifica del progetto!'
            }
            show={
                type === 'create' ?
                    createProjectMutation.isError :
                    editProjectMutation.isError
            }
            onHide={
                type === 'create' ?
                    createProjectMutation.reset :
                    editProjectMutation.reset
            }
        />
    </>
}

export default EditModal